// NextScene — Advertise With Us (industry lead form).
// OTP-gated submission → POST /leads/advertise. No pricing shown.
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { Logo } from "@/components/Logo";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ShieldCheck, ArrowLeft, Megaphone, Film, Music, GraduationCap, Users, Camera, Sparkles, Wrench, Shirt } from "lucide-react";
import { COUNTRIES, DIAL_CODES } from "@/lib/countries";

const CATEGORIES = [
  { label: "Brand Campaigns",      icon: Megaphone },
  { label: "OTT Releases",         icon: Film },
  { label: "Music Launches",       icon: Music },
  { label: "Acting & Modeling",    icon: GraduationCap },
  { label: "Casting Calls",        icon: Users },
  { label: "Talent Hunts",         icon: Sparkles },
  { label: "Workshops & Events",   icon: Users },
  { label: "Creator Tools",        icon: Wrench },
  { label: "Camera / Audio",       icon: Camera },
  { label: "Fashion & Beauty",     icon: Shirt },
  { label: "& Many More",          icon: Sparkles },
];

const OBJECTIVES = ["Brand Awareness","App Downloads","Course Sales","Event Promotion","Casting Promotion","Product Sales","Other"];
const PLACEMENTS = ["Homepage Banner","Sponsored Talent Card","Casting Promotion","Social Media Mention","Newsletter Feature","Other"];

export default function Advertise() {
  const nav = useNavigate();
  const [dial, setDial] = useState("+91");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpToken, setOtpToken] = useState("");
  const [otpHint, setOtpHint] = useState("");
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [fullName, setFullName] = useState("");
  const [company, setCompany] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("India");
  const [countryOther, setCountryOther] = useState("");
  const [website, setWebsite] = useState("");
  const [email, setEmail] = useState("");
  const [objective, setObjective] = useState("");
  const [objectiveOther, setObjectiveOther] = useState("");
  const [placement, setPlacement] = useState("");
  const [placementOther, setPlacementOther] = useState("");
  const [audience, setAudience] = useState("");
  const [startDate, setStartDate] = useState("");
  const [duration, setDuration] = useState("");
  const [estBudget, setEstBudget] = useState("");
  const [creative, setCreative] = useState("");
  const [details, setDetails] = useState("");

  const canSendOtp = phone.length >= 6 && phone.length <= 15 && !busy;
  const mandatoryOk = fullName.trim() && company.trim() && city.trim() && country.trim() && (country !== "Other" || countryOther.trim());
  const canSubmit = otpVerified && mandatoryOk && !busy;

  const sendOtp = async () => {
    if (!canSendOtp) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/phone/send-otp", { phone_number: `${dial}${phone}` });
      setOtpSent(true);
      setOtpHint(data.hint || "");
      toast.success(data.hint ? "OTP sent (MOCK)" : "OTP sent to your phone");
    } catch (err) { toast.error(errMsg(err, "Could not send OTP")); }
    finally { setBusy(false); }
  };
  const verifyOtpAndMark = async () => {
    if (otp.length !== 6) return toast.error("Enter the 6-digit OTP");
    setBusy(true);
    try {
      const { data } = await api.post("/leads/verify-otp", { phone_number: `${dial}${phone}`, code: otp });
      setOtpToken(data.verification_token);
      setOtpVerified(true);
      toast.success("Mobile number verified");
    } catch (err) { toast.error(errMsg(err, "Invalid or expired OTP")); }
    finally { setBusy(false); }
  };
  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      await api.post("/leads/advertise", {
        phone_number: `${dial}${phone}`, country_code: dial,
        otp_verification_token: otpToken,
        full_name: fullName.trim(), company: company.trim(),
        city: city.trim(), country: country.trim(), country_other: countryOther.trim(),
        website: website.trim(), email: email.trim(),
        objective, objective_other: objectiveOther.trim(),
        preferred_placement: placement, placement_other: placementOther.trim(),
        target_audience: audience.trim(),
        start_date: startDate, duration: duration.trim(),
        estimated_budget: estBudget.trim(),
        creative_link: creative.trim(),
        details: details.trim(),
      });
      setSubmitted(true);
    } catch (err) { toast.error(errMsg(err, "Could not submit — please try again")); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ backgroundColor: "#070709", minHeight: "100vh", color: "#F5F3EE" }}>
      <div className="max-w-5xl mx-auto px-5 md:px-8 pt-8 pb-6 flex items-center justify-between">
        <Link to="/" className="inline-flex items-center gap-2 text-[rgba(255,255,255,0.72)] hover:text-white"><ArrowLeft size={16}/> Back</Link>
        <Logo size="sm"/>
      </div>
      <div className="max-w-4xl mx-auto px-5 md:px-8 pb-24">
        {submitted ? (
          <div style={{ marginTop: "80px", padding: "48px 36px", borderRadius: "28px", border: "1px solid rgba(199,154,43,0.32)", background: "linear-gradient(180deg, rgba(199,154,43,0.06) 0%, rgba(7,7,9,0) 100%)", textAlign: "center" }}>
            <div style={{ width: "56px", height: "56px", margin: "0 auto 18px", borderRadius: "999px", border: "1px solid #C79A2B", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              <CheckCircle2 size={26} color="#C79A2B"/>
            </div>
            <h2 style={{ fontFamily: "'Ogg', 'Ogg Regular', 'Didot', 'Playfair Display', Georgia, serif", fontWeight: 400, fontSize: "36px", color: "#F5F3EE", margin: 0, letterSpacing: "-1px" }}>
              Thank you.
            </h2>
            <p style={{ marginTop: "12px", fontFamily: "'Inter'", fontSize: "15px", color: "rgba(255,255,255,0.78)", lineHeight: 1.6 }}>
              Thank you for your interest in advertising with Nextscene. Our team will review your inquiry and share a suitable proposal shortly.
            </p>
            <button type="button" onClick={() => nav("/")} style={{ marginTop: "22px", height: "44px", padding: "0 22px", borderRadius: "999px", background: "transparent", color: "#F5F3EE", fontFamily: "'Inter'", fontWeight: 500, fontSize: "14px", border: "1px solid rgba(199,154,43,0.55)", cursor: "pointer" }}>
              Back to Home
            </button>
          </div>
        ) : (
          <>
            <h1 style={{ fontFamily: "'Ogg', 'Ogg Regular', 'Didot', 'Bodoni Moda', 'Playfair Display', Georgia, serif", fontWeight: 400, fontSize: "clamp(40px, 5vw, 68px)", lineHeight: 1.02, letterSpacing: "-1.5px", margin: 0 }}>
              Advertise <span style={{ fontStyle: "italic", color: "#C79A2B" }}>With Us</span>
            </h1>
            <p style={{ marginTop: "14px", fontFamily: "'Inter'", fontSize: "16px", color: "rgba(255,255,255,0.72)", lineHeight: 1.6, maxWidth: "640px" }}>
              Reach creators, performers, and entertainment audiences through Nextscene.
            </p>

            {/* Category icon cards */}
            <div className="grid md:grid-cols-4" style={{ marginTop: "36px", gap: "12px", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))" }}>
              {CATEGORIES.map(({ label, icon: Icon }) => (
                <div key={label} style={{ padding: "16px", borderRadius: "18px", border: "1px solid rgba(199,154,43,0.20)", background: "rgba(199,154,43,0.03)", display: "flex", alignItems: "center", gap: "10px" }}>
                  <div style={{ width: "34px", height: "34px", borderRadius: "999px", border: "1px solid rgba(199,154,43,0.55)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon size={16} color="#C79A2B"/>
                  </div>
                  <span style={{ fontFamily: "'Inter'", fontSize: "13px", color: "#F5F3EE" }}>{label}</span>
                </div>
              ))}
            </div>

            <div className="grid md:grid-cols-2" style={{ marginTop: "40px", gap: "18px" }}>
              <Field label="Full Name *"><input data-testid="ad-name" className="ns-form-input" value={fullName} onChange={(e)=>setFullName(e.target.value)} maxLength={120}/></Field>
              <Field label="Company / Brand *"><input data-testid="ad-company" className="ns-form-input" value={company} onChange={(e)=>setCompany(e.target.value)} maxLength={160}/></Field>
              <Field label="City *"><input data-testid="ad-city" className="ns-form-input" value={city} onChange={(e)=>setCity(e.target.value)} maxLength={80}/></Field>
              <Field label="Country *">
                <select data-testid="ad-country" className="ns-form-select" value={country} onChange={(e)=>setCountry(e.target.value)}>
                  {COUNTRIES.map(c => <option key={c} value={c} style={{background:"#0B0B0D"}}>{c}</option>)}
                  <option value="Other" style={{background:"#0B0B0D"}}>Other</option>
                </select>
                {country === "Other" && (
                  <input className="ns-form-input" style={{marginTop:"10px"}} placeholder="Specify country" value={countryOther} onChange={(e)=>setCountryOther(e.target.value)} maxLength={120}/>
                )}
              </Field>
            </div>

            {/* Mobile + OTP */}
            <div style={{ marginTop: "18px" }}>
              <label className="ns-form-label">Mobile Number *</label>
              <div className="flex items-stretch" style={{ gap: "10px", flexWrap: "wrap" }}>
                <select value={dial} onChange={(e)=>setDial(e.target.value)} disabled={otpVerified} data-testid="ad-dial"
                  className="ns-form-select" style={{ width: "auto", minWidth: "150px", flexShrink: 0 }}>
                  {DIAL_CODES.map(d => (
                    <option key={`${d.name}-${d.code}`} value={d.code} style={{background:"#0B0B0D"}}>
                      {d.flag} {d.name} {d.code}
                    </option>
                  ))}
                </select>
                <input type="tel" inputMode="numeric" maxLength={15} className="ns-form-input" data-testid="ad-phone" style={{ flex: 1, minWidth: "180px" }}
                  placeholder="Mobile number" value={phone} onChange={(e)=>setPhone(e.target.value.replace(/\D/g,""))} disabled={otpVerified}/>
                <button type="button" onClick={sendOtp} disabled={!canSendOtp || otpVerified} data-testid="ad-send-otp"
                  style={{ borderRadius: "14px", padding: "0 18px", background: canSendOtp && !otpVerified ? "#C79A2B" : "rgba(199,154,43,0.35)", color: "#070709", fontWeight: 600, fontFamily: "'Inter'", fontSize: "14px", border: "none", cursor: canSendOtp && !otpVerified ? "pointer" : "not-allowed", whiteSpace: "nowrap" }}>
                  {otpSent ? "Resend OTP" : "Send OTP"}
                </button>
              </div>
              {otpSent && !otpVerified && (
                <div className="flex items-stretch" style={{ marginTop: "10px", gap: "10px" }}>
                  <input type="tel" inputMode="numeric" maxLength={6} className="ns-form-input" data-testid="ad-otp"
                    placeholder="6-digit code" value={otp} onChange={(e)=>setOtp(e.target.value.replace(/\D/g,""))}
                    style={{ letterSpacing: "0.35em", textAlign: "center", fontSize: "18px" }}/>
                  <button type="button" onClick={verifyOtpAndMark} data-testid="ad-verify-otp"
                    style={{ borderRadius: "14px", padding: "0 18px", background: "transparent", color: "#C79A2B", fontWeight: 600, fontFamily: "'Inter'", fontSize: "14px", border: "1px solid #C79A2B", cursor: "pointer", whiteSpace: "nowrap" }}>
                    Verify
                  </button>
                </div>
              )}
              {otpHint && !otpVerified && (
                <p style={{ marginTop: "6px", fontSize: "12px", color: "#F4C550" }}>MOCK: use {otpHint.replace(/^MOCK OTP:\s*/, "")}</p>
              )}
              {otpVerified && (
                <p style={{ marginTop: "8px", fontSize: "13px", color: "#C79A2B", display: "inline-flex", alignItems: "center", gap: "6px" }}>
                  <CheckCircle2 size={14}/> Mobile verified
                </p>
              )}
            </div>

            <div className="grid md:grid-cols-2" style={{ marginTop: "18px", gap: "18px" }}>
              <Field label="Company Website / Instagram"><input className="ns-form-input" value={website} onChange={(e)=>setWebsite(e.target.value)} maxLength={300}/></Field>
              <Field label="Email Address"><input type="email" className="ns-form-input" value={email} onChange={(e)=>setEmail(e.target.value)} maxLength={200}/></Field>
              <Field label="Campaign Objective">
                <select className="ns-form-select" value={objective} onChange={(e)=>setObjective(e.target.value)}>
                  <option value="" style={{background:"#0B0B0D"}}>Select</option>
                  {OBJECTIVES.map(o => <option key={o} value={o} style={{background:"#0B0B0D"}}>{o}</option>)}
                </select>
                {objective === "Other" && (
                  <input className="ns-form-input" style={{marginTop:"10px"}} placeholder="Specify objective" value={objectiveOther} onChange={(e)=>setObjectiveOther(e.target.value)} maxLength={120}/>
                )}
              </Field>
              <Field label="Preferred Placement">
                <select className="ns-form-select" value={placement} onChange={(e)=>setPlacement(e.target.value)}>
                  <option value="" style={{background:"#0B0B0D"}}>Select</option>
                  {PLACEMENTS.map(p => <option key={p} value={p} style={{background:"#0B0B0D"}}>{p}</option>)}
                </select>
                {placement === "Other" && (
                  <input className="ns-form-input" style={{marginTop:"10px"}} placeholder="Specify placement" value={placementOther} onChange={(e)=>setPlacementOther(e.target.value)} maxLength={120}/>
                )}
              </Field>
              <Field label="Campaign Start Date"><input type="date" className="ns-form-input" value={startDate} onChange={(e)=>setStartDate(e.target.value)}/></Field>
              <Field label="Campaign Duration"><input className="ns-form-input" placeholder="e.g., 2 weeks" value={duration} onChange={(e)=>setDuration(e.target.value)} maxLength={60}/></Field>
              <Field label="Estimated Budget"><input className="ns-form-input" placeholder="e.g., ₹5L" value={estBudget} onChange={(e)=>setEstBudget(e.target.value)} maxLength={60}/></Field>
              <Field label="Creative Link (Image / Video / PDF)"><input className="ns-form-input" placeholder="Paste a share link" value={creative} onChange={(e)=>setCreative(e.target.value)} maxLength={400}/></Field>
              <Field label="Target Audience" full><input className="ns-form-input" value={audience} onChange={(e)=>setAudience(e.target.value)} maxLength={400}/></Field>
              <Field label="Campaign Details" full><textarea className="ns-form-textarea" value={details} onChange={(e)=>setDetails(e.target.value)} maxLength={4000}/></Field>
            </div>

            <button type="button" onClick={submit} disabled={!canSubmit} data-testid="ad-submit"
              style={{ marginTop: "28px", width: "100%", height: "58px", borderRadius: "999px",
                background: canSubmit ? "#C79A2B" : "rgba(199,154,43,0.35)",
                color: "#070709", fontFamily: "'Inter'", fontWeight: 600, fontSize: "16px",
                border: "none", cursor: canSubmit ? "pointer" : "not-allowed",
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "10px",
                boxShadow: canSubmit ? "0 10px 30px rgba(199,154,43,0.28), 0 0 28px rgba(199,154,43,0.18)" : "none" }}>
              {busy ? <Loader2 size={18} className="animate-spin"/> : <ShieldCheck size={18}/>}
              Request Advertising Proposal
            </button>
            {!otpVerified && (
              <p style={{ marginTop: "10px", textAlign: "center", fontSize: "12px", color: "rgba(255,255,255,0.6)", fontFamily: "'Inter'" }}>
                Verify your mobile number to enable submission.
              </p>
            )}
          </>
        )}
      </div>
      <Footer/>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div style={{ gridColumn: full ? "1 / -1" : undefined }}>
      <label className="ns-form-label">{label}</label>
      {children}
    </div>
  );
}
